import { assign, setup } from 'xstate';

/** One boolean per trip index (`true` = call when ready, `false` = pick date & time). */
export type WillCallContext = {
  values: boolean[];
  focusedIndex: number | null;
};

export type WillCallEvent =
  | { type: 'CHANGE'; index: number; value: boolean }
  | { type: 'RESIZE'; count: number }
  | { type: 'RESET' }
  | { type: 'FOCUS'; index: number }
  | { type: 'BLUR'; index: number };

function resizeValues(prev: boolean[], count: number): boolean[] {
  const c = Math.max(1, count);
  const next = prev.slice(0, c);
  while (next.length < c) next.push(false);
  return next;
}

export const willCallMachine = setup({
  types: {
    context: {} as WillCallContext,
    events: {} as WillCallEvent,
  },
}).createMachine({
  id: 'willCall',
  context: { values: [false], focusedIndex: null },
  initial: 'idle',
  states: {
    idle: {
      on: {
        FOCUS: {
          target: 'focused',
          actions: assign({
            focusedIndex: ({ event }) =>
              event.type === 'FOCUS' ? event.index : null,
          }),
        },
        CHANGE: {
          actions: assign({
            values: ({ context, event }) => {
              if (event.type !== 'CHANGE') return context.values;
              const next = [...context.values];
              while (next.length <= event.index) next.push(false);
              next[event.index] = event.value;
              return next;
            },
          }),
        },
        RESIZE: {
          actions: assign({
            values: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.values, event.count)
                : context.values,
          }),
        },
      },
    },
    focused: {
      on: {
        CHANGE: {
          actions: assign({
            values: ({ context, event }) => {
              if (event.type !== 'CHANGE') return context.values;
              const next = [...context.values];
              while (next.length <= event.index) next.push(false);
              next[event.index] = event.value;
              return next;
            },
          }),
        },
        RESIZE: {
          actions: assign({
            values: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.values, event.count)
                : context.values,
          }),
        },
        BLUR: {
          target: 'idle',
          actions: assign({
            focusedIndex: ({ context, event }) =>
              event.type === 'BLUR' && context.focusedIndex === event.index
                ? null
                : context.focusedIndex,
          }),
        },
        FOCUS: {
          actions: assign({
            focusedIndex: ({ event }) =>
              event.type === 'FOCUS' ? event.index : null,
          }),
        },
      },
    },
  },
  on: {
    RESET: {
      target: '.idle',
      actions: assign({ values: [false], focusedIndex: null }),
    },
  },
});
